<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>自提点管理</span>
        <div class="header-actions">
          <el-button :loading="loading" @click="loadData">刷新</el-button>
          <el-button type="primary" @click="openCreate">新增自提点</el-button>
        </div>
      </div>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="自提点数据加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以检查后端服务状态后再重试。</span>
          <el-button type="text" @click="loadData">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-empty v-if="!loading && !list.length" description="还没有自提点数据">
          <el-button type="primary" @click="openCreate">去新增</el-button>
        </el-empty>

        <el-table v-else :data="list" border v-loading="loading">
          <el-table-column prop="name" label="自提点名称" min-width="180" />
          <el-table-column label="联系人 / 电话" min-width="180">
            <template slot-scope="{ row }">
              <div>{{ row.contact || '未设置' }}</div>
              <div class="sub-text">{{ row.mobile }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="address" label="地址" min-width="260" />
          <el-table-column label="营业时间" min-width="160">
            <template slot-scope="{ row }">
              {{ row.businessHours || '未设置' }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }">
              <el-tag :type="row.status ? 'success' : 'info'">
                {{ row.status ? '启用' : '停用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="sort" label="排序" width="90" />
          <el-table-column label="操作" width="180" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" @click="editRow(row)">编辑</el-button>
              <el-button type="text" @click="removeRow(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </el-card>

    <el-dialog
      :title="form.id ? '编辑自提点' : '新增自提点'"
      :visible.sync="visible"
      width="720px"
      @closed="resetForm"
    >
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="100px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="名称" prop="name">
              <el-input v-model.trim="form.name" maxlength="60" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系人">
              <el-input v-model.trim="form.contact" maxlength="30" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="联系电话" prop="mobile">
              <el-input v-model.trim="form.mobile" maxlength="20" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="营业时间">
              <el-input
                v-model.trim="form.businessHours"
                maxlength="60"
                placeholder="例如：9:00-18:00"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="地址" prop="address">
          <el-input v-model.trim="form.address" maxlength="200" show-word-limit />
        </el-form-item>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="纬度">
              <el-input-number
                v-model="form.latitude"
                :precision="6"
                :step="0.000001"
                :controls="false"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="经度">
              <el-input-number
                v-model="form.longitude"
                :precision="6"
                :step="0.000001"
                :controls="false"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="排序" prop="sort">
              <el-input-number v-model="form.sort" :min="0" :precision="0" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态">
              <el-switch v-model="form.status" :active-value="1" :inactive-value="0" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="备注">
          <el-input v-model.trim="form.remark" type="textarea" :rows="4" maxlength="200" show-word-limit />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" @click="submit">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { addPickupStore, deletePickupStore, getPickupStoreList, updatePickupStore } from '../../api/admin'

const createForm = () => ({
  id: null,
  name: '',
  contact: '',
  mobile: '',
  address: '',
  latitude: null,
  longitude: null,
  businessHours: '',
  sort: 1,
  status: 1,
  remark: ''
})

export default {
  data() {
    return {
      list: [],
      loading: false,
      loadError: false,
      visible: false,
      form: createForm(),
      formRules: {
        name: [{ required: true, message: '请填写自提点名称', trigger: 'blur' }],
        mobile: [{ required: true, message: '请填写联系电话', trigger: 'blur' }],
        address: [{ required: true, message: '请填写自提点地址', trigger: 'blur' }],
        sort: [{ required: true, message: '请填写排序值', trigger: 'change' }]
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        this.list = await getPickupStoreList()
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = createForm()
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate()
        }
      })
      this.visible = true
    },
    editRow(row) {
      this.form = {
        ...createForm(),
        ...row
      }
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate()
        }
      })
      this.visible = true
    },
    resetForm() {
      this.form = createForm()
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields()
      }
    },
    async submit() {
      const valid = await new Promise(resolve => {
        this.$refs.formRef.validate(ok => resolve(ok))
      })
      if (!valid) {
        return
      }
      const payload = {
        ...this.form,
        latitude: this.form.latitude === null || this.form.latitude === undefined ? null : Number(this.form.latitude),
        longitude: this.form.longitude === null || this.form.longitude === undefined ? null : Number(this.form.longitude),
        sort: Number(this.form.sort || 0),
        status: Number(this.form.status || 0)
      }
      try {
        if (payload.id) {
          await updatePickupStore(payload)
        } else {
          await addPickupStore(payload)
        }
        this.$message.success('保存成功')
        this.visible = false
        this.loadData()
      } catch (error) {
        this.$message.error((error && error.message) || '保存失败，请稍后重试')
      }
    },
    removeRow(row) {
      this.$confirm(`确认删除“${row.name}”吗？`, '删除自提点', {
        type: 'warning'
      }).then(async () => {
        try {
          await deletePickupStore(row.id)
          this.$message.success('已删除')
          this.loadData()
        } catch (error) {
          this.$message.error((error && error.message) || '删除失败，请稍后重试')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.state-block {
  margin-bottom: 16px;
}

.sub-text {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}
</style>
